import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Shield, Lock, Unlock, AlertTriangle, 
  Activity, Users, Database, MapPin, 
  ArrowUpRight, Globe, Server, Eye,
  Link2, Target, Clock, Crosshair
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { vaultConfig } from "../data/mock";
import { Link } from "react-router-dom";
import trackingService from "../services/trackingService";

const AnalyticsPage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(false);

  // SEO: No Index
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    trackingService.initialize();
    
    // Track analytics page access
    trackingService.track('analytics_page_access', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
  }, []);

  // Fetch live data when unlocked
  useEffect(() => {
    if (unlocked) {
      fetchLiveData();
      
      // Set up real-time updates every 30 seconds
      const interval = setInterval(fetchLiveData, 30000);
      return () => clearInterval(interval);
    }
  }, [unlocked]);

  const fetchLiveData = async () => {
    try {
      setLoading(true);
      const data = await trackingService.getLiveAnalytics();
      if (data) {
        setLiveData(data);
      }
    } catch (error) {
      console.error('Failed to fetch live analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (password === vaultConfig.password) { // "Itzgreen"
      setUnlocked(true);
      setError(false);
      
      // Track successful vault access
      await trackingService.trackVaultAccess(password, true);
      
      // Fetch initial live data
      await fetchLiveData();
    } else {
      setError(true);
      
      // Track failed vault access
      await trackingService.trackVaultAccess(password, false);
      
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <main className="min-h-screen bg-[rgb(10,10,12)] text-white font-mono selection:bg-[rgb(218,255,1)] selection:text-black">
      {/* Background Grid */}
      <div
        className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <AnimatePresence mode="wait">
          {!unlocked ? (
            /* ═══════════════ THE GATE (LOGIN SCREEN) ═══════════════ */
            <motion.div
              key="locked"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05, filter: "blur(10px)" }}
              className="min-h-[80vh] flex flex-col items-center justify-center max-w-md mx-auto"
            >
              <div className="w-16 h-16 bg-[rgba(218,255,1,0.1)] border border-[rgb(218,255,1)] rounded-full flex items-center justify-center mb-8 animate-pulse">
                <Shield size={32} className="text-[rgb(218,255,1)]" />
              </div>

              <h1 className="text-2xl font-bold tracking-[0.2em] mb-2 text-[rgb(218,255,1)]">
                COMMAND TERMINAL
              </h1>
              <p className="text-[rgb(100,100,110)] text-xs tracking-widest mb-10">
                RESTRICTED EXECUTIVE ACCESS
              </p>

              <form onSubmit={handleUnlock} className="w-full space-y-4">
                <div className="relative">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="ENTER ACCESS KEY"
                    className={`bg-[rgb(20,20,25)] border-2 text-center text-lg tracking-[0.3em] h-14 rounded-none transition-all focus:ring-0 ${
                      error
                        ? "border-red-500 text-red-500 placeholder:text-red-900/50"
                        : "border-[rgb(63,63,63)] focus:border-[rgb(218,255,1)] text-[rgb(218,255,1)] placeholder:text-[rgb(60,60,70)]"
                    }`}
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[rgb(218,255,1)] text-black font-bold tracking-[0.2em] h-12 rounded-none hover:bg-white transition-colors"
                >
                  <Unlock size={16} className="mr-3" />
                  AUTHENTICATE
                </Button>
              </form>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 flex items-center gap-2 text-red-500 text-xs tracking-widest"
                >
                  <AlertTriangle size={14} />
                  <span>ACCESS DENIED: INVALID CREDENTIALS</span>
                </motion.div>
              )}
            </motion.div>
          ) : (
            /* ═══════════════ DASHBOARD (UNLOCKED) ═══════════════ */
            <motion.div
              key="dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header */}
              <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-[rgba(255,255,255,0.1)] pb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[rgb(218,255,1)] animate-pulse" />
                    <span className="text-[rgb(218,255,1)] text-xs tracking-[0.2em] font-bold">
                      LIVE TERMINAL ACTIVE
                    </span>
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight text-white">
                    EXECUTIVE ANALYTICS
                  </h1>
                </div>
                <div className="flex items-center gap-4 text-xs text-[rgb(100,100,110)]">
                  <div className="flex items-center gap-2 px-4 py-2 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.1)] rounded-md">
                    <Server size={14} />
                    <span>SERVER: US-EAST-1</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.1)] rounded-md">
                    <Shield size={14} className="text-[rgb(74,222,128)]" />
                    <span>SECURE CONNECTION</span>
                  </div>
                  <Link to="/admin/link-generator" data-testid="nav-link-generator">
                    <div className="flex items-center gap-2 px-4 py-2 bg-[rgba(218,255,1,0.06)] border border-[rgba(218,255,1,0.3)] rounded-md cursor-pointer hover:bg-[rgba(218,255,1,0.12)] transition-colors">
                      <Link2 size={14} className="text-[rgb(218,255,1)]" />
                      <span className="text-[rgb(218,255,1)]">LINK GENERATOR</span>
                    </div>
                  </Link>
                  <Link to="/admin/briefs" data-testid="nav-briefs">
                    <div className="flex items-center gap-2 px-4 py-2 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.1)] rounded-md cursor-pointer hover:border-[rgba(218,255,1,0.3)] transition-colors">
                      <Crosshair size={14} className="text-[rgb(218,255,1)]" />
                      <span className="text-[rgb(161,161,170)]">BRIEFS</span>
                    </div>
                  </Link>
                </div>
              </header>

              {/* KPI Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                {(liveData?.kpi || []).map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`bg-[rgb(20,20,25)] border p-6 rounded-lg relative overflow-hidden group ${
                      item.status === 'quarantine' 
                        ? 'border-[rgba(239,68,68,0.3)]' 
                        : 'border-[rgba(255,255,255,0.08)]'
                    }`}
                  >
                    <div className={`absolute top-0 left-0 w-full h-[2px] ${
                      item.status === 'quarantine'
                        ? 'bg-gradient-to-r from-red-500 to-transparent'
                        : 'bg-gradient-to-r from-[rgb(218,255,1)] to-transparent'
                    } opacity-50`} />
                    
                    <div className="flex justify-between items-start mb-4">
                      <span className={`text-[10px] tracking-[0.2em] font-bold ${
                        item.status === 'quarantine' ? 'text-red-400' : 'text-[rgb(100,100,110)]'
                      }`}>
                        {item.label}
                      </span>
                      {item.status === 'quarantine' 
                        ? <AlertTriangle size={16} className="text-red-500" />
                        : <Activity size={16} className="text-[rgb(218,255,1)]" />
                      }
                    </div>
                    
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-white">
                        {item.value}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        item.status === 'quarantine'
                          ? 'text-red-400 bg-[rgba(239,68,68,0.1)]'
                          : 'text-[rgb(74,222,128)] bg-[rgba(74,222,128,0.1)]'
                      }`}>
                        {item.change}
                      </span>
                    </div>
                  </motion.div>
                ))}
                
                {/* Loading state */}
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="col-span-full text-center py-8"
                  >
                    <div className="inline-flex items-center gap-2 text-[rgb(218,255,1)]">
                      <div className="w-4 h-4 border-2 border-[rgb(218,255,1)] border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs tracking-[0.2em]">UPDATING SURVEILLANCE DATA...</span>
                    </div>
                  </motion.div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Executive Access Ledger */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="lg:col-span-2 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden"
                >
                  <div className="p-6 border-b border-[rgba(255,255,255,0.08)] flex justify-between items-center">
                    <h3 className="text-sm font-bold tracking-[0.15em] flex items-center gap-2">
                      <Database size={16} className="text-[rgb(218,255,1)]" />
                      ACCESS LEDGER
                    </h3>
                    <Badge variant="outline" className="border-[rgb(63,63,63)] text-[rgb(161,161,170)] text-[10px]">
                      REAL-TIME
                    </Badge>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-[rgb(26,26,32)] text-[rgb(100,100,110)] border-b border-[rgba(255,255,255,0.05)]">
                        <tr>
                          <th className="px-6 py-4 font-medium tracking-wider">TIMESTAMP</th>
                          <th className="px-6 py-4 font-medium tracking-wider">LOCATION</th>
                          <th className="px-6 py-4 font-medium tracking-wider">SOURCE</th>
                          <th className="px-6 py-4 font-medium tracking-wider">ACTION</th>
                          <th className="px-6 py-4 font-medium tracking-wider">DWELL</th>
                          <th className="px-6 py-4 font-medium tracking-wider text-right">USER ID (EST.)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[rgba(255,255,255,0.03)]">
                        {(liveData?.ledger || []).map((row) => (
                          <tr key={row.id} className="hover:bg-[rgba(218,255,1,0.02)] transition-colors">
                            <td className="px-6 py-4 text-[rgb(161,161,170)] font-mono">{row.timestamp}</td>
                            <td className="px-6 py-4 text-white flex items-center gap-2">
                              <MapPin size={12} className="text-[rgb(63,63,63)]" />
                              {row.location}
                            </td>
                            <td className="px-6 py-4 text-[rgb(161,161,170)]">{row.source}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-bold ${
                                row.action.includes("Vault") 
                                  ? "bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)]" 
                                  : "bg-[rgba(255,255,255,0.05)] text-white"
                              }`}>
                                {row.action}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 font-mono text-[10px] ${
                                row.dwell && row.dwell.includes('m') 
                                  ? 'text-[rgb(218,255,1)]' 
                                  : row.dwell && row.dwell.includes('h')
                                    ? 'text-[rgb(74,222,128)]'
                                    : 'text-[rgb(100,100,110)]'
                              }`}>
                                <Clock size={10} />
                                {row.dwell || '—'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right text-[rgb(100,100,110)] font-mono">{row.user}</td>
                          </tr>
                        ))}
                        
                        {/* Show message if no live data */}
                        {(!liveData?.ledger || liveData.ledger.length === 0) && (
                          <tr>
                            <td colSpan="6" className="px-6 py-8 text-center text-[rgb(100,100,110)]">
                              <div className="flex flex-col items-center gap-2">
                                <Eye size={20} className="text-[rgb(218,255,1)]" />
                                <span className="text-xs tracking-[0.15em]">SURVEILLANCE PERIMETER ACTIVE</span>
                                <span className="text-[10px] opacity-60">Monitoring for executive activity...</span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </motion.div>

                {/* Geographic Heatmap List */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.08)] rounded-lg p-6"
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-sm font-bold tracking-[0.15em] flex items-center gap-2">
                      <Globe size={16} className="text-[rgb(218,255,1)]" />
                      TRAFFIC ORIGIN
                    </h3>
                  </div>

                  <div className="space-y-6">
                    {(liveData?.geo || []).map((city, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-white font-medium">{city.city}</span>
                          <span className="text-[rgb(161,161,170)]">{city.count} hits</span>
                        </div>
                        <div className="h-1.5 w-full bg-[rgb(30,30,35)] rounded-full overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${city.percentage}%` }}
                            transition={{ duration: 1, delay: 0.5 + (i * 0.1) }}
                            className="h-full bg-[rgb(218,255,1)]"
                          />
                        </div>
                      </div>
                    ))}
                    
                    {/* Show message if no geographic data */}
                    {(!liveData?.geo || liveData.geo.length === 0) && (
                      <div className="text-center py-8">
                        <Globe size={20} className="text-[rgb(218,255,1)] mx-auto mb-2" />
                        <p className="text-[rgb(161,161,170)] text-[10px] leading-relaxed">
                          GEOGRAPHIC TRACKING ACTIVE<br/>
                          Awaiting executive traffic...
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.08)]">
                    <div className="bg-[rgba(218,255,1,0.05)] border border-[rgba(218,255,1,0.2)] rounded p-4 text-center">
                      <Eye size={20} className="text-[rgb(218,255,1)] mx-auto mb-2" />
                      <p className="text-[rgb(161,161,170)] text-[10px] leading-relaxed">
                        TRACKING ENGINE ACTIVE<br/>
                        Monitoring Los Angeles & NY IP Ranges
                      </p>
                      {liveData?.lastUpdated && (
                        <p className="text-[rgb(100,100,110)] text-[8px] mt-2">
                          Last Updated: {new Date(liveData.lastUpdated).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>

              </div>

              {/* ═══════════════ REF LINK INTELLIGENCE ═══════════════ */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 bg-[rgb(20,20,25)] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden"
                data-testid="ref-intelligence-panel"
              >
                <div className="p-6 border-b border-[rgba(255,255,255,0.08)] flex justify-between items-center">
                  <h3 className="text-sm font-bold tracking-[0.15em] flex items-center gap-2">
                    <Crosshair size={16} className="text-[rgb(218,255,1)]" />
                    REF LINK INTELLIGENCE
                  </h3>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="border-[rgb(63,63,63)] text-[rgb(161,161,170)] text-[10px]">
                      ?ref= DECODER
                    </Badge>
                    <Badge variant="outline" className="border-[rgba(218,255,1,0.3)] text-[rgb(218,255,1)] text-[10px]">
                      {(liveData?.refSummary || []).length} TARGETS TRACKED
                    </Badge>
                  </div>
                </div>

                {/* Ref Summary Cards */}
                {(liveData?.refSummary || []).length > 0 ? (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {(liveData?.refSummary || []).map((ref, i) => (
                        <motion.div
                          key={ref.ref}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.6 + (i * 0.05) }}
                          className="bg-[rgb(26,26,32)] border border-[rgba(218,255,1,0.15)] rounded-lg p-4 relative overflow-hidden"
                          data-testid={`ref-card-${ref.ref}`}
                        >
                          <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[rgb(218,255,1)] via-[rgba(218,255,1,0.3)] to-transparent" />
                          
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Target size={14} className="text-[rgb(218,255,1)]" />
                              <span className="text-[rgb(218,255,1)] text-[10px] tracking-[0.15em] font-bold font-mono">
                                ?ref={ref.ref}
                              </span>
                            </div>
                            <span className="text-white text-lg font-bold">{ref.hits}</span>
                          </div>

                          <p className="text-white text-xs font-medium mb-2">{ref.target}</p>
                          
                          <div className="space-y-1.5 text-[10px]">
                            <div className="flex items-center gap-1.5 text-[rgb(161,161,170)]">
                              <Clock size={10} />
                              <span>Last: {new Date(ref.lastSeen).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}</span>
                            </div>
                            {ref.cities.length > 0 && (
                              <div className="flex items-center gap-1.5 text-[rgb(161,161,170)]">
                                <MapPin size={10} />
                                <span>{ref.cities.join(', ')}</span>
                              </div>
                            )}
                            {ref.paths.length > 0 && (
                              <div className="flex items-center gap-1.5 text-[rgb(161,161,170)]">
                                <Link2 size={10} />
                                <span>{ref.paths.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Raw Ref Log Table */}
                    <div className="border-t border-[rgba(255,255,255,0.05)] pt-4">
                      <h4 className="text-[10px] tracking-[0.2em] text-[rgb(100,100,110)] font-bold mb-3">RAW INTERCEPTS</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-[rgb(26,26,32)] text-[rgb(100,100,110)] border-b border-[rgba(255,255,255,0.05)]">
                            <tr>
                              <th className="px-4 py-3 font-medium tracking-wider">TIMESTAMP</th>
                              <th className="px-4 py-3 font-medium tracking-wider">REF CODE</th>
                              <th className="px-4 py-3 font-medium tracking-wider">DECODED TARGET</th>
                              <th className="px-4 py-3 font-medium tracking-wider">CITY</th>
                              <th className="px-4 py-3 font-medium tracking-wider">ENTRY PATH</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[rgba(255,255,255,0.03)]">
                            {(liveData?.refIntelligence || []).slice(0, 15).map((entry, i) => (
                              <tr key={entry.id || i} className="hover:bg-[rgba(218,255,1,0.02)] transition-colors">
                                <td className="px-4 py-3 text-[rgb(161,161,170)] font-mono text-[11px]">
                                  {new Date(entry.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)] px-2 py-0.5 rounded text-[10px] font-bold font-mono">
                                    {entry.ref}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-white font-medium">{entry.target}</td>
                                <td className="px-4 py-3 text-[rgb(161,161,170)] flex items-center gap-1.5">
                                  <MapPin size={10} className="text-[rgb(63,63,63)]" />
                                  {entry.city}{entry.region ? `, ${entry.region}` : ''}
                                </td>
                                <td className="px-4 py-3 text-[rgb(100,100,110)] font-mono text-[11px]">{entry.path}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[rgba(218,255,1,0.05)] border border-[rgba(218,255,1,0.2)] mb-4">
                      <Crosshair size={24} className="text-[rgb(218,255,1)]" />
                    </div>
                    <h4 className="text-white text-sm font-bold tracking-[0.1em] mb-2">REF DECODER ARMED</h4>
                    <p className="text-[rgb(100,100,110)] text-xs leading-relaxed max-w-md mx-auto mb-6">
                      No ref link intercepts detected yet. When an executive clicks an outbound pitch link 
                      containing a <span className="text-[rgb(218,255,1)] font-mono">?ref=</span> parameter, 
                      their target identity, timestamp, and geographic origin will appear here in real-time.
                    </p>
                    <div className="bg-[rgb(26,26,32)] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 max-w-lg mx-auto">
                      <p className="text-[10px] text-[rgb(100,100,110)] tracking-[0.15em] mb-2">EXAMPLE PITCH LINKS:</p>
                      <div className="space-y-1.5 text-[11px] font-mono text-left">
                        <p className="text-[rgb(161,161,170)]">itzgreenmusic.com/vault<span className="text-[rgb(218,255,1)]">?ref=MACRO</span></p>
                        <p className="text-[rgb(161,161,170)]">itzgreenmusic.com/catalog<span className="text-[rgb(218,255,1)]">?ref=sdiazmatos</span></p>
                        <p className="text-[rgb(161,161,170)]">itzgreenmusic.com/<span className="text-[rgb(218,255,1)]">?ref=HOORAE_Q2</span></p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
};

export default AnalyticsPage;
